/**
 * PATH: apps/qa/src/pages/area-execution/useAreaExecution.ts
 * PURPOSE: Custom hook encapsulating queries, mutations, and handlers for area execution
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type { ItemInstance, Evidence } from '@/types/qa'
import { toUIStatus } from './area-execution-types'
import type { BackendItemStatus, UIItemStatus } from './area-execution-types'
import {
  fetchAreaDetails, fetchAreaItems, fetchAreaEvidence, fetchTemplates,
  updateItemStatus, updateAreaWorkStatus, updateItemReading, lockArea, unlockArea, applyTemplate,
  requestHoldPoint, createNCR, addItemNote, updateItemNote, deleteItemNote,
  verifyItemCompletion, verifyAreaCompletion, attachItemPhoto,
} from './area-execution-api'

export function useAreaExecution() {
  const { areaId } = useParams<{ areaId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [activeIndex, setActiveIndex] = useState(0)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [lockModalOpen, setLockModalOpen] = useState(false)
  const [ncrModalOpen, setNcrModalOpen] = useState(false)
  const [holdPointModalOpen, setHoldPointModalOpen] = useState(false)
  const [areaSignOffModalOpen, setAreaSignOffModalOpen] = useState(false)
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null)
  const [isUploadingDoc, setIsUploadingDoc] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Queries
  const { data: area, isLoading: areaLoading } = useQuery({ queryKey: ['qa-area', areaId], queryFn: () => fetchAreaDetails(areaId!), enabled: !!areaId })
  const { data: items = [], isLoading: itemsLoading } = useQuery({ queryKey: ['qa-area-items', areaId], queryFn: () => fetchAreaItems(areaId!), enabled: !!areaId })
  const { data: evidence = [] } = useQuery({ queryKey: ['qa-area-evidence', areaId], queryFn: () => fetchAreaEvidence(areaId!), enabled: !!areaId })
  const { data: templates = [] } = useQuery({ queryKey: ['qa-templates'], queryFn: fetchTemplates })

  // Mutations
  const statusMutation = useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: UIItemStatus | BackendItemStatus }) => updateItemStatus(itemId, status),
    onSuccess: (updated) => {
      queryClient.setQueryData(['qa-area-items', areaId], (old: ItemInstance[] | undefined) => old?.map(i => (i.id === updated.id ? updated : i)) || [])
      // The backend starts the area when an item leaves not_started, so refetch
      // the area or its badge keeps showing the stale work status.
      queryClient.invalidateQueries({ queryKey: ['qa-area', areaId] })
    },
  })
  const areaStatusMutation = useMutation({
    mutationFn: (workStatus: BackendItemStatus) => updateAreaWorkStatus(areaId!, workStatus),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-area', areaId] }) },
  })
  // Notes are a running record, so add is the default and edit/delete stay
  // available on a specific row rather than being inferred from "is there
  // already a note?" the way the single-note field had to.
  const noteMutation = useMutation({
    mutationFn: (input:
      | { op: 'add'; itemId: string; noteText: string }
      | { op: 'edit'; noteId: string; noteText: string }
      | { op: 'delete'; noteId: string }
    ) => {
      if (input.op === 'delete') return deleteItemNote(input.noteId).then(() => null)
      if (input.op === 'edit') return updateItemNote(input.noteId, input.noteText)
      return addItemNote(areaId!, input.itemId, input.noteText)
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-area-evidence', areaId] }) },
  })
  // Invalidates rather than writing the response into the cache: reopening a
  // row clears its verification server-side, so the badge must always come from
  // a fresh read of both the item list and the area.
  const verificationMutation = useMutation({
    mutationFn: (input:
      | { scope: 'item'; itemId: string; verified: boolean }
      | { scope: 'area'; verified: boolean }
    ) => input.scope === 'item'
      ? verifyItemCompletion(input.itemId, input.verified)
      : verifyAreaCompletion(areaId!, input.verified),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-area-items', areaId] })
      queryClient.invalidateQueries({ queryKey: ['qa-area', areaId] })
    },
  })
  const readingMutation = useMutation({
    mutationFn: ({ itemId, value, unit }: { itemId: string; value: string; unit: string }) => updateItemReading(itemId, value, unit),
    onSuccess: (updated) => { queryClient.setQueryData(['qa-area-items', areaId], (old: ItemInstance[] | undefined) => old?.map(i => (i.id === updated.id ? updated : i)) || []) },
  })

  const lockMutation = useMutation({
    mutationFn: ({ isLocked, reason }: { isLocked: boolean; reason: string }) => isLocked ? unlockArea(areaId!, reason) : lockArea(areaId!, reason),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-area', areaId] }); setLockModalOpen(false) },
  })

  const templateMutation = useMutation({
    mutationFn: (templateId: string) => applyTemplate(templateId, [areaId!]),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-area-items', areaId] }); setTemplateModalOpen(false) },
  })

  const holdPointMutation = useMutation({
    mutationFn: (name: string) => requestHoldPoint(areaId!, name, items[activeIndex]?.id),
    onSuccess: () => setHoldPointModalOpen(false),
  })

  const areaSignOffMutation = useMutation({
    mutationFn: (name: string) => requestHoldPoint(areaId!, name),
    onSuccess: () => { setAreaSignOffModalOpen(false); queryClient.invalidateQueries({ queryKey: ['qa-hold-points'] }) },
  })

  const ncrMutation = useMutation({
    mutationFn: (data: { title: string; description: string; severity: string }) =>
      createNCR({ job_id: area?.job_id || '', inspection_id: area?.inspection_id || '', area_id: areaId!, item_instance_id: items[activeIndex]?.id, ...data }),
    onSuccess: () => setNcrModalOpen(false),
  })

  // Document upload
  const handleDocumentUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || !areaId) return
    setIsUploadingDoc(true)
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        const formData = new FormData()
        formData.append('file', file); formData.append('purpose', 'qa_evidence')
        const uploadRes = await api().post('/files/upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        const isImage = file.type.startsWith('image/'); const isVideo = file.type.startsWith('video/')
        await api().post('/qa/evidence/', { area: areaId, type: isImage ? 'photo' : isVideo ? 'video' : 'document', asset_id: uploadRes.data.id, description: file.name, folder: 'Field Documents' })
      } catch (err) { if (import.meta.env.DEV) console.error(`[QA] Failed to upload "${file.name}":`, err) }
    }
    setIsUploadingDoc(false)
    queryClient.invalidateQueries({ queryKey: ['qa-area-evidence', areaId] })
  }, [areaId, queryClient])

  // Navigation helpers
  const advanceToNext = useCallback(() => {
    const next = items.findIndex((item, idx) => idx > activeIndex && toUIStatus(item.status) === 'pending')
    if (next !== -1) setActiveIndex(next)
    else if (activeIndex < items.length - 1) setActiveIndex(i => i + 1)
  }, [activeIndex, items])

  const handleStatusChange = useCallback((status: UIItemStatus | BackendItemStatus) => {
    const curr = items[activeIndex]; if (!curr || area?.is_locked) return
    statusMutation.mutate({ itemId: curr.id, status }); advanceToNext()
  }, [items, activeIndex, area?.is_locked, statusMutation, advanceToNext])

  const handleAreaStatusChange = useCallback((status: BackendItemStatus) => {
    if (!areaId || area?.is_locked) return
    areaStatusMutation.mutate(status)
  }, [areaId, area?.is_locked, areaStatusMutation])

  // mutateAsync, not mutate: QaNoteThread only keeps a failed draft on screen if
  // the rejection reaches it, and a note typed on site is the thing we must not
  // lose (BUG-2026-08-31-QA-HUB-PHOTO-SAVED-NOTHING, same failure family).
  const handleAddNote = useCallback(async (text: string) => {
    const curr = items[activeIndex]
    if (!curr || !areaId) return
    await noteMutation.mutateAsync({ op: 'add', itemId: curr.id, noteText: text })
  }, [items, activeIndex, areaId, noteMutation])

  const handleEditNote = useCallback(async (noteId: string, text: string) => {
    await noteMutation.mutateAsync({ op: 'edit', noteId, noteText: text })
  }, [noteMutation])

  const handleDeleteNote = useCallback(async (noteId: string) => {
    await noteMutation.mutateAsync({ op: 'delete', noteId })
  }, [noteMutation])

  const handleDeleteEvidence = useCallback(async (evidenceId: string) => {
    await deleteItemNote(evidenceId)
    queryClient.invalidateQueries({ queryKey: ['qa-area-evidence', areaId] })
  }, [areaId, queryClient])

  // mutateAsync so a refusal reaches QaOwnerVerification, which shows it rather
  // than letting the badge silently stay on No.
  const handleVerifyItem = useCallback(async (itemId: string, verified: boolean) => {
    await verificationMutation.mutateAsync({ scope: 'item', itemId, verified })
  }, [verificationMutation])

  const handleVerifyArea = useCallback(async (verified: boolean) => {
    if (!areaId) return
    await verificationMutation.mutateAsync({ scope: 'area', verified })
  }, [areaId, verificationMutation])

  const handleReadingChange = useCallback((value: string, unit: string) => {
    const curr = items[activeIndex]; if (!curr || area?.is_locked) return
    readingMutation.mutate({ itemId: curr.id, value, unit })
  }, [items, activeIndex, area?.is_locked, readingMutation])

  const handleTakePhoto = useCallback(() => { if (items[activeIndex]) setCameraOpen(true) }, [items, activeIndex])

  const handlePickPhotoFile = useCallback(async (file: File) => {
    const curr = items[activeIndex]
    if (!curr || !areaId) return
    const formData = new FormData()
    formData.append('file', file)
    formData.append('purpose', 'qa_evidence')
    const uploadRes = await api().post('/files/upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    await attachItemPhoto(areaId, curr.id, uploadRes.data.id, file.name)
    queryClient.invalidateQueries({ queryKey: ['qa-area-evidence', areaId] })
  }, [items, activeIndex, areaId, queryClient])

  const handlePickPhotoAsset = useCallback(async (assetId: string, filename?: string) => {
    const curr = items[activeIndex]
    if (!curr || !areaId) return
    await attachItemPhoto(areaId, curr.id, assetId, filename)
    queryClient.invalidateQueries({ queryKey: ['qa-area-evidence', areaId] })
  }, [items, activeIndex, areaId, queryClient])

  const refreshItems = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['qa-area-items', areaId] })
  }, [areaId, queryClient])

  const handleCameraCapture = useCallback((_evidence?: unknown) => {
    queryClient.invalidateQueries({ queryKey: ['qa-area-evidence', areaId] }); setCameraOpen(false)
  }, [areaId, queryClient])

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      const curr = items[activeIndex]; if (!curr || area?.is_locked) return
      switch (e.key.toLowerCase()) {
        case 'p': e.preventDefault(); statusMutation.mutate({ itemId: curr.id, status: 'done' }); advanceToNext(); break
        case 'f': e.preventDefault(); statusMutation.mutate({ itemId: curr.id, status: 'failed' }); advanceToNext(); break
        case 'n': e.preventDefault(); statusMutation.mutate({ itemId: curr.id, status: 'na' }); advanceToNext(); break
        case 'arrowdown': case 'j': e.preventDefault(); setActiveIndex(i => Math.min(i + 1, items.length - 1)); break
        case 'arrowup': case 'k': e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); break
      }
    }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey)
  }, [activeIndex, items, area?.is_locked, statusMutation, advanceToNext])

  // Scroll active into view
  useEffect(() => {
    if (listRef.current && items.length > 0) {
      const el = listRef.current.children[activeIndex] as HTMLElement
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [activeIndex, items.length])

  const allComplete = useMemo(() => items.length > 0 && items.every(i => ['done', 'failed', 'not_applicable'].includes(i.status)), [items])

  return {
    areaId, area, items, evidence, templates, navigate,
    areaLoading, itemsLoading, activeIndex, setActiveIndex,
    cameraOpen, setCameraOpen, templateModalOpen, setTemplateModalOpen,
    lockModalOpen, setLockModalOpen, ncrModalOpen, setNcrModalOpen,
    holdPointModalOpen, setHoldPointModalOpen, areaSignOffModalOpen, setAreaSignOffModalOpen,
    selectedEvidence, setSelectedEvidence, isUploadingDoc, listRef, fileInputRef,
    statusMutation, readingMutation, lockMutation, templateMutation, holdPointMutation, areaSignOffMutation, ncrMutation,
    verificationMutation,
    handleDocumentUpload, handleStatusChange, handleAreaStatusChange, handleAddNote, handleEditNote, handleDeleteNote, handleReadingChange, handleTakePhoto, handleCameraCapture, allComplete,
    handleVerifyItem, handleVerifyArea,
    handlePickPhotoFile, handlePickPhotoAsset, handleDeleteEvidence, refreshItems,
  }
}
